import {
	type RectWallName,
	resolveMonoHighWall,
} from "../../domain/roof/rectRoofGeometry";
import {
	getRoofKindLabel,
	getRoofPitchLimits,
} from "../../domain/roof/roofConfig";
import { getRoofSystemLabel } from "../../domain/roof/roofSystems";
import type { RoofConfig } from "../../engines/types";
import { useT } from "../../i18n";

function withoutHighWall<T extends { highWall?: RectWallName }>(config: T): T {
	const copy = { ...config };
	delete copy.highWall;
	return copy;
}

interface RoofConfigEditorProps {
	roofConfig: RoofConfig;
	onRoofConfigChange: (config: RoofConfig) => void;
}

export function RoofConfigEditor({
	roofConfig,
	onRoofConfigChange,
}: RoofConfigEditorProps) {
	const t = useT();
	if (roofConfig.kind === "barrel") {
		return null;
	}
	const limits = getRoofPitchLimits(roofConfig.kind);
	// Under 14° a felt roof needs a special membrane; double-seamed band
	// roofing is fine from 1:10 (≈5.7°, AMA Hus), so the warning is moot there.
	const isLowSlopeMono =
		roofConfig.kind === "mono" &&
		roofConfig.pitchDeg < 14 &&
		!(roofConfig.systemId === "sheet_metal" && roofConfig.pitchDeg >= 6);

	const updateKind = (kind: RoofConfig["kind"]) => {
		const nextKind = kind === "gable" ? "gable" : "mono";
		const nextLimits = getRoofPitchLimits(nextKind);
		// Only a mono roof has a high side; a gable drops it.
		onRoofConfigChange({
			...(nextKind === "mono" ? roofConfig : withoutHighWall(roofConfig)),
			kind: nextKind,
			pitchDeg: Math.min(
				nextLimits.max,
				Math.max(nextLimits.min, roofConfig.pitchDeg),
			),
		});
	};

	return (
		<div className="inspector-section-body">
			<div className="builder-controls roof-config-editor">
				<div className="input-row">
					<label htmlFor="roof-kind">{t.roof_typeLabel}</label>
					<select
						id="roof-kind"
						value={roofConfig.kind}
						onChange={(event) =>
							updateKind(event.target.value as RoofConfig["kind"])
						}
					>
						<option value="mono">{getRoofKindLabel("mono")}</option>
						<option value="gable">{getRoofKindLabel("gable")}</option>
					</select>
				</div>

				{roofConfig.kind === "mono" && (
					<div className="input-row">
						<label htmlFor="roof-high-wall">{t.roof_highWallLabel}</label>
						<select
							id="roof-high-wall"
							value={resolveMonoHighWall(roofConfig)}
							onChange={(event) => {
								const highWall = event.target.value as RectWallName;
								const rest = withoutHighWall(roofConfig);
								// `front` is the default and is stored as absent.
								onRoofConfigChange(
									highWall === "front" ? rest : { ...rest, highWall },
								);
							}}
						>
							<option value="front">{t.roof_highWallFront}</option>
							<option value="back">{t.roof_highWallBack}</option>
							<option value="left">{t.roof_highWallLeft}</option>
							<option value="right">{t.roof_highWallRight}</option>
						</select>
					</div>
				)}

				<div className="slider-row">
					<div className="slider-label-row">
						<span className="slider-label">{t.roof_pitchLabel}</span>
						<span className="slider-value">{roofConfig.pitchDeg}°</span>
					</div>
					<input
						type="range"
						className="slider-track"
						aria-label={t.roof_pitchAriaLabel.replace(
							"{value}",
							String(roofConfig.pitchDeg),
						)}
						min={limits.min}
						max={limits.max}
						step={1}
						value={roofConfig.pitchDeg}
						onChange={(event) =>
							onRoofConfigChange({
								...roofConfig,
								pitchDeg: Number(event.target.value),
							})
						}
					/>
					<div className="slider-range-labels">
						<span>{limits.min}°</span>
						<span>{limits.max}°</span>
					</div>
				</div>

				<div className="input-row">
					<label htmlFor="roof-system">{t.roof_systemLabel}</label>
					<select
						id="roof-system"
						value={roofConfig.systemId}
						onChange={(event) =>
							onRoofConfigChange({
								...roofConfig,
								systemId: event.target.value as typeof roofConfig.systemId,
							})
						}
					>
						<option value="felt_roof">{getRoofSystemLabel("felt_roof")}</option>
						<option value="sheet_metal">
							{getRoofSystemLabel("sheet_metal")}
						</option>
					</select>
				</div>

				<p className="site-conditions-note">
					{roofConfig.kind === "gable" ? t.roof_gableDesc : t.roof_monoDesc}{" "}
					{t.roof_packageHint}
				</p>

				{isLowSlopeMono && (
					<p className="site-conditions-note" style={{ color: "#b45309" }}>
						{t.roof_lowSlopeWarning}
					</p>
				)}
			</div>
		</div>
	);
}
